import { useInfiniteScroll } from 'data/hooks/useInfiniteScroll'
import { useQuery, UseQueryArgs } from 'urql'
import merge from 'lodash/merge'
import { useDestinationFetching } from './useDestinationFetching'
import { isServer } from 'data/helpers/ssr'
import { useMemo } from 'react'
import { joinBy } from '@ama-selections/ui'
import { SelectedDestinationData } from 'components/popovers/DestinationSelectionPopover/_types'

export const useDestinationPageQuery = (
  paginatorInfoLocation: string,
  propertyQuery: UseQueryArgs,
  destinationQuery: UseQueryArgs,
) => {
  const {
    data: propertyData,
    fetching: propertyFetch,
    ...infiniteScroll
  } = useInfiniteScroll(
    paginatorInfoLocation,
    {
      query: propertyQuery.query,
      variables: {
        ...propertyQuery.variables,
        perPage: 6,
      },
      pause: isServer,
    },
  )

  const [{ data: destinationData, fetching: destinationFetch }] = useQuery(destinationQuery)

  const fetching = useMemo(() => propertyFetch || destinationFetch, [propertyFetch, destinationFetch])

  useDestinationFetching(fetching, infiniteScroll.page)

  const initialDestination = useMemo(() => {
    const destination = destinationData?.country
      ?? destinationData?.region
      ?? destinationData?.subRegion

    if (!destination) {
      return undefined
    }

    switch (destination.__typename) {
      case 'DestinationCountry':
        return {
          country: {
            value: destination?.value,
            slug: destination?.slug,
          },
          value: destination?.value,
        }
      case 'DestinationRegion':
        return {
          country: {
            value: destination.country.value,
            slug: destination.country.slug,
          },
          region: {
            value: destination.value,
            slug: destination.slug,
          },
          value: joinBy([
            destination?.value,
            destination?.country?.value,
          ]),
        }
      case 'DestinationSubRegion':
        return {
          country: {
            value: destination.region.country.value,
            slug: destination.region.country.slug,
          },
          region: {
            value: destination.region.value,
            slug: destination.region.slug,
          },
          subregion: {
            value: destination.value,
            slug: destination.slug,
          },
          value: joinBy([
            destination?.value,
            destination?.region?.value,
            destination?.region?.country?.value,
          ]),
        }
      default:
        return undefined
    }
  }, [destinationData])

  return {
    data: merge(propertyData, destinationData),
    fetching: fetching || isServer,
    initialDestination: initialDestination as SelectedDestinationData | undefined,
    ...infiniteScroll,
  }
}
